import type { Asset, Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import {
    getPrePackagedMorphPosition,
    getRootAssetGroup,
    hasAdjacentMorphEnabledScene,
} from 'js/src/luminary/luminary-selectors';

export type MorphTechnique = 'true-morph' | 'match-cut';

export type MorphTechniqueId = 'text';

export type MorphAssetCategory = 'text' | 'media' | 'icon' | 'shape' | 'lottie' | 'group';

export type MorphPairing = {
    sourceId: string;
    targetId: string;
    technique: MorphTechnique;
};

export type MorphDisqualifiedReason =
    | 'no-morphable-assets'
    | 'incompatible-structure'
    | 'below-threshold'
    | 'shares-morphable-content'
    | 'consecutive-morph-limit'
    | 'scene-sequence';

export type MorphRecommendation = {
    recommended: boolean;
    score: number;
    techniqueId: MorphTechniqueId | null;
    disqualifiedReason: MorphDisqualifiedReason | null;
    pairings: MorphPairing[];
};

export const DEFAULT_MORPH_SCORE_THRESHOLD = 0.5;

// A text match-cut needs visible travel — below this fraction of the canvas diagonal it reads as a
// blink in place rather than a cut, so the technique is disqualified.
const MIN_TEXT_TRAVEL_FRACTION = 0.04;

export function classifyMorphAsset(asset: Asset): MorphAssetCategory | null {
    if (VideoData.isTextAsset(asset)) return 'text';
    if (VideoData.isMediaAsset(asset)) return 'media';
    if (VideoData.isIconAsset(asset)) return 'icon';
    if (VideoData.isLottieAsset(asset)) return 'lottie';
    if (VideoData.isAssetGroup(asset)) return 'group';
    if (
        VideoData.isCircleAsset(asset) ||
        VideoData.isRectangleAsset(asset) ||
        VideoData.isGradientAsset(asset)
    ) {
        return 'shape';
    }
    return null;
}

export function getMorphableAssets(scene: Scene): Asset[] {
    return scene.getAssets().filter((asset) => classifyMorphAsset(asset) !== null);
}

// A match-cut would fight assets the base morph already animates (same image by uuid, matching icons,
// identical text), so we disqualify rather than stack the two. Empty text is ignored: unused placeholder
// slots match by template position but render nothing (mirrors the non-empty filter in getTextMorphUnits).
function sharesNativelyMorphingAsset(sources: Asset[], targets: Asset[]): boolean {
    const rendersVisibleContent = (asset: Asset) => !VideoData.isTextAsset(asset) || asset.hasText;
    const visibleTargets = targets.filter(rendersVisibleContent);
    return sources
        .filter(rendersVisibleContent)
        .some((source) => visibleTargets.some((target) => source.isMorphMatch(target)));
}

function referenceDiagonal(scene: Scene): number {
    const { width, height } = scene.root.referenceResolution;
    return Math.hypot(width, height);
}

// The rendered visual center, NOT transformation.position — design-laid-out text sits at
// transformation.position (0,0) with its real position coming from the layout, so only getBounds
// reflects where it actually is.
function visualCenter(asset: Asset): { x: number; y: number } {
    const bounds = asset.getBounds();
    return { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
}

function distanceBetween(a: Asset, b: Asset): number {
    const centerA = visualCenter(a);
    const centerB = visualCenter(b);
    return Math.hypot(centerA.x - centerB.x, centerA.y - centerB.y);
}

function travelFraction(source: Asset, target: Asset, diagonal: number): number {
    if (diagonal === 0) return 0;
    return distanceBetween(source, target) / diagonal;
}

// Sources are matched in reading order to their nearest remaining target, so paths can cross in
// asymmetric layouts; a leftover source or target when counts differ is frozen in place by the apply.
function nearestNeighbourPairs(
    sources: Asset[],
    targets: Asset[],
): { source: Asset; target: Asset }[] {
    const readingOrder = (a: Asset, b: Asset) => {
        const centerA = visualCenter(a);
        const centerB = visualCenter(b);
        return centerA.y - centerB.y || centerA.x - centerB.x;
    };

    const availableTargets = [...targets];
    const pairs: { source: Asset; target: Asset }[] = [];

    for (const source of [...sources].sort(readingOrder)) {
        if (availableTargets.length === 0) break;
        const nearest = availableTargets.reduce(
            (best, target, index) => {
                const distance = distanceBetween(source, target);
                return distance < best.distance ? { index, distance } : best;
            },
            { index: 0, distance: Infinity },
        );
        pairs.push({ source, target: availableTargets[nearest.index] });
        availableTargets.splice(nearest.index, 1);
    }

    return pairs;
}

// The morph unit for each text is its top-most enclosing GROUP (or the text itself if ungrouped).
// We morph the group, not the bare text leaf: a group's `asset.x` (bounds edge) moves with it, so
// the slide is correct, whereas a grouped text leaf has `asset.x = 0` (layout-driven) and flies off.
// Stickers are excluded — match-cut duplicates are themselves stickers, so this stops a re-run from
// re-pairing them.
export function getTextMorphUnits(scene: Scene): Asset[] {
    const units = new Set<Asset>();
    scene
        .getAssets({ deep: true })
        .filter(
            (asset) =>
                VideoData.isTextAsset(asset) && asset.hasText && !VideoData.isStickerAsset(asset),
        )
        .forEach((leaf) => units.add(getRootAssetGroup(leaf) ?? leaf));
    return [...units];
}

type TechniqueResult = { score: number; pairings: MorphPairing[] };

// Size/area is deliberately not a scoring factor — a match-cut reads fine between differently-sized text.
function scoreTextTechnique(sourceScene: Scene, targetScene: Scene): TechniqueResult {
    const sources = getTextMorphUnits(sourceScene);
    const targets = getTextMorphUnits(targetScene);
    if (sources.length === 0 || targets.length === 0) {
        return { score: 0, pairings: [] };
    }

    const pairs = nearestNeighbourPairs(sources, targets);
    const diagonal = referenceDiagonal(sourceScene);

    const countMatch =
        Math.min(sources.length, targets.length) / Math.max(sources.length, targets.length);
    const averageTravel =
        pairs.reduce((sum, pair) => sum + travelFraction(pair.source, pair.target, diagonal), 0) /
        pairs.length;
    const travelSufficiency = averageTravel >= MIN_TEXT_TRAVEL_FRACTION ? 1 : 0;

    const score = countMatch * travelSufficiency;

    const pairings: MorphPairing[] = pairs.map(({ source, target }) => ({
        sourceId: source.id,
        targetId: target.id,
        technique: source.isMorphMatch(target) ? 'true-morph' : 'match-cut',
    }));

    return { score, pairings };
}

export function scoreScenePairForMorph(
    sourceScene: Scene,
    targetScene: Scene,
    threshold: number = DEFAULT_MORPH_SCORE_THRESHOLD,
): MorphRecommendation {
    const sources = getMorphableAssets(sourceScene);
    const targets = getMorphableAssets(targetScene);

    if (sources.length === 0 || targets.length === 0) {
        return {
            recommended: false,
            score: 0,
            techniqueId: null,
            disqualifiedReason: 'no-morphable-assets',
            pairings: [],
        };
    }

    if (sharesNativelyMorphingAsset(sources, targets)) {
        return {
            recommended: false,
            score: 0,
            techniqueId: null,
            disqualifiedReason: 'shares-morphable-content',
            pairings: [],
        };
    }

    const text = scoreTextTechnique(sourceScene, targetScene);

    if (text.pairings.length === 0) {
        return {
            recommended: false,
            score: 0,
            techniqueId: null,
            disqualifiedReason: 'incompatible-structure',
            pairings: [],
        };
    }

    if (text.score < threshold) {
        return {
            recommended: false,
            score: text.score,
            techniqueId: 'text',
            disqualifiedReason: 'below-threshold',
            pairings: text.pairings,
        };
    }

    // Only text is paired/scored. Every non-text asset (media/icons/charts) is frozen-cut by the apply
    // as a snapshot of the next scene — it follows the cut rather than being paired here.
    return {
        recommended: true,
        score: text.score,
        techniqueId: 'text',
        disqualifiedReason: null,
        pairings: text.pairings,
    };
}

export type MorphGapState = {
    morphSceneId: string | null;
    isMorphEnabled: boolean;
    isRecommended: boolean;
};

// Morph lives on the gap's "from" scene (intoScene's predecessor).
export function getMorphGapState(intoScene: Scene): MorphGapState {
    const morphScene = intoScene.prevScene;
    if (!morphScene) {
        return { morphSceneId: null, isMorphEnabled: false, isRecommended: false };
    }
    if (
        getPrePackagedMorphPosition(morphScene) !== null ||
        getPrePackagedMorphPosition(intoScene) !== null
    ) {
        return { morphSceneId: morphScene.id, isMorphEnabled: false, isRecommended: false };
    }
    if (hasAdjacentMorphEnabledScene(morphScene)) {
        return {
            morphSceneId: morphScene.id,
            isMorphEnabled: morphScene.morphEnabled,
            isRecommended: false,
        };
    }
    const recommendation = scoreScenePairForMorph(morphScene, intoScene);
    return {
        morphSceneId: morphScene.id,
        isMorphEnabled: morphScene.morphEnabled,
        isRecommended: recommendation.recommended,
    };
}
